import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('carfleet_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me')
        .then(res => setUser(res.data.user))
        .catch(() => {
          localStorage.removeItem('carfleet_token');
          delete api.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    if (res.data.twoFaRequired || res.data.totpRequired) return res.data; // { twoFaRequired: true, userId }

    const { token, user } = res.data;
    localStorage.setItem('carfleet_token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return { success: true };
  }, []);

  const verify2FA = useCallback(async (userId, token) => {
    const res = await api.post('/auth/verify-2fa', { userId, token });
    const { token: jwt, user } = res.data;
    localStorage.setItem('carfleet_token', jwt);
    api.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
    setUser(user);
    return true;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('carfleet_token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(perm) ?? false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verify2FA, hasPermission, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
